import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api from "../../Utils/Axios";
import * as XLSX from "xlsx";
function ReceivableKhata() {
  const navigate = useNavigate();
  const [ledgerParties, setLedgerParties] = useState([]);
  const [selectedLedgerParty, setSelectedLedgerParty] = useState(null);
  const [ledgerTransactions, setLedgerTransactions] = useState([]);
  const [isLoadingLedger, setIsLoadingLedger] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [companyLanguage, setCompanyLanguage] = useState('en');
  const [company, setCompany] = useState(null);
  const [totalReceivable, setTotalReceivable] = useState(0);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const printRef = useRef(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);

    // Set default date range (current month)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setDateRange({
      startDate: firstDay.toLocaleDateString('en-CA'),
      endDate: lastDay.toLocaleDateString('en-CA')
    });

    fetchReceivableParties();
  }, []);

  const getToken = () => {
    return localStorage.getItem("companyToken");
  };

  const fetchReceivableParties = async () => {
    try {
      setIsLoadingLedger(true);
      const token = getToken();
      if (!token) {
        navigate("/company/login");
        return;
      }

      // Fetch all parties (both customers and suppliers)
      const response = await api.get('/ledger/parties', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const allParties = response.data.parties || [];
      // Filter only receivable parties (positive balance)
      const receivableParties = allParties.filter(party => {
        const balance = party.currentBalance || 0;
        return balance > 0;
      });

      setLedgerParties(receivableParties);

      // Calculate total receivable
      const total = receivableParties.reduce((sum, party) => {
        return sum + (party.currentBalance || 0);
      }, 0);
      setTotalReceivable(total);

      // Don't auto-select first party, let user click to see transactions
      setSelectedLedgerParty(null);
      setLedgerTransactions([]);
    } catch (error) {
      console.error('Fetch receivable parties error:', error);
      toast.error(companyLanguage === 'ur' ? 'قابل وصول کھاتہ نہیں مل سکا' : 'Failed to load receivable ledger');
    } finally {
      setIsLoadingLedger(false);
    }
  };

  const fetchLedgerTransactions = async (partyId) => {
    if (!partyId) return;
    try {
      setIsLoadingLedger(true);
      const token = getToken();
      const response = await api.get(`/ledger/parties/${partyId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedLedgerParty(response.data.party);
      setLedgerTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Fetch ledger transactions error:', error);
      toast.error(companyLanguage === 'ur' ? 'ٹرانزیکشنز نہیں مل سکیں' : 'Failed to load transactions');
    } finally {
      setIsLoadingLedger(false);
    }
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    if (!printRef.current || !selectedLedgerParty) {
      toast.error(isUrdu ? 'مواد نہیں ملا' : 'Content not found');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info(isUrdu ? 'PDF بنایا جا رہا ہے...' : 'Generating PDF...');

      const element = printRef.current;

      // Show print headers (elements with hidden print:block)
      const printHeaders = element.querySelectorAll('.hidden.print\\:block, [class*="hidden print:block"]');
      printHeaders.forEach(header => {
        header.style.display = 'block';
      });

      // Hide elements that should be hidden in print
      const hiddenElements = element.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
      hiddenElements.forEach(el => {
        el.style.display = 'none';
      });

      // Add temporary styles for PDF - matching print styles exactly
      const style = document.createElement('style');
      style.id = 'pdf-print-styles';
      style.textContent = `
        .print\\:hidden {
          display: none !important;
        }
        .print\\:col-span-full {
          grid-column: 1 / -1 !important;
        }
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          background: white !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .grid {
          display: block !important;
        }
        .md\\:grid-cols-3 {
          display: block !important;
        }
        .bg-white {
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 0.5cm !important;
        }
        .rounded-lg, .rounded-xl {
          border-radius: 0 !important;
        }
        .shadow-lg, .shadow-2xl {
          box-shadow: none !important;
        }
        table {
          width: 100% !important;
          max-width: 100% !important;
          table-layout: fixed !important;
          border-collapse: collapse !important;
        }
        table th {
          font-weight: 600 !important;
          font-size: 12px !important;
          padding: 8px 6px !important;
          text-align: inherit !important;
          white-space: nowrap !important;
          overflow: visible !important;
          text-overflow: clip !important;
        }
        table th.font-urdu, table th[class*="font-urdu"] {
          text-transform: none !important;
          letter-spacing: normal !important;
          font-family: 'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif !important;
        }
        table td {
          font-size: 11px !important;
          padding: 8px 6px !important;
          word-wrap: break-word !important;
        }
        .font-urdu, [class*="font-urdu"] {
          font-family: 'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif !important;
          font-feature-settings: normal !important;
          text-rendering: optimizeLegibility !important;
          -webkit-font-smoothing: antialiased !important;
          -moz-osx-font-smoothing: grayscale !important;
        }
        .overflow-x-auto {
          width: 100% !important;
          overflow: visible !important;
        }
        div[ref] {
          width: 100% !important;
          max-width: 100% !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .bg-gradient-to-br {
          background: white !important;
        }
        .bg-brand-background {
          background: white !important;
        }
        * {
          box-sizing: border-box !important;
        }
      `;
      document.head.appendChild(style);

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        allowTaint: true
      });

      // Restore original display states
      printHeaders.forEach(header => {
        header.style.display = '';
      });
      hiddenElements.forEach(el => {
        el.style.display = '';
      });

      // Remove temporary styles
      const tempStyle = document.getElementById('pdf-print-styles');
      if (tempStyle) {
        document.head.removeChild(tempStyle);
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `Receivable_Ledger_${new Date().toLocaleDateString('en-CA')}.pdf`;

      pdf.save(fileName);
      toast.success(isUrdu ? 'PDF ڈاؤن لوڈ ہو گیا!' : 'PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(isUrdu ? 'PDF بنانے میں ناکام' : 'Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Download Excel
  const handleDownloadExcel = () => {
    if (ledgerParties.length === 0) {
      toast.error(isUrdu ? 'کوئی ڈیٹا نہیں' : 'No data to export');
      return;
    }

    try {
      const excelData = [];

      // Add header
      excelData.push([
        isUrdu ? 'نام' : 'Name',
        isUrdu ? 'قسم' : 'Type',
        isUrdu ? 'فون' : 'Phone',
        isUrdu ? 'قابل وصول' : 'Receivable Amount'
      ]);

      // Filter parties by search
      const filteredParties = ledgerParties.filter(party => {
        const searchTerm = searchFilter.toLowerCase();
        return (
          party.name?.toLowerCase().includes(searchTerm) ||
          party.nameUrdu?.toLowerCase().includes(searchTerm) ||
          party.phone?.toLowerCase().includes(searchTerm)
        );
      });

      // Add data rows
      filteredParties.forEach(party => {
        excelData.push([
          isUrdu ? (party.nameUrdu || party.name) : (party.name || party.nameUrdu),
          party.type === 'customer' ? (isUrdu ? 'گاہک' : 'Customer') : (isUrdu ? 'سپلائر' : 'Supplier'),
          party.phone || '-',
          (party.currentBalance || 0).toLocaleString()
        ]);
      });

      // Add total row
      excelData.push([
        isUrdu ? 'کل' : 'Total',
        '',
        '',
        filteredParties.reduce((sum, party) => sum + (party.currentBalance || 0), 0).toLocaleString()
      ]);

      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, isUrdu ? 'قابل وصول' : 'Receivable');

      // Generate filename
      const fileName = `Receivable_Ledger_${new Date().toLocaleDateString('en-CA')}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success(isUrdu ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : 'Excel file downloaded successfully');
    } catch (error) {
      console.error('Excel download error:', error);
      toast.error(isUrdu ? 'Excel ڈاؤن لوڈ میں ناکام' : 'Failed to download Excel');
    }
  };

  // Print function
  const handlePrint = () => {
    window.print();
  };

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      <Helmet><title>{isUrdu ? 'قابل وصول کھاتہ' : 'Receivable Ledger'} - Company Dashboard</title></Helmet>

      <div className={`min-h-screen bg-brand-background p-4 md:p-8 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}} dir={isUrdu ? 'rtl' : 'ltr'}>
        <div className="max-w-7xl mx-auto">
          {/* PDF/Print Container */}
          <div ref={printRef} className="print:w-full print:max-w-full print:m-0 print:p-0">
            {/* Print Header - Company Info - Top of Page */}
            {company && (
              <div className="hidden print:block border-b-2 border-gray-400 pb-3 mb-4 bg-white p-4">
                <div className="flex items-start justify-between mb-2 gap-4">
                  {/* Company Name - Top Left */}
                  <div className="text-left flex-shrink-0">
                    <h1
                      className={`text-xl font-bold ${isUrdu ? 'font-urdu' : ''}`}
                      style={{
                        ...(isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}),
                        color: '#1e40af',
                        WebkitPrintColorAdjust: 'exact',
                        printColorAdjust: 'exact'
                      }}
                    >
                      {company?.shopName || company?.username || company?.shopNameUrdu || 'Company Name'}
                    </h1>
                    {company?.username && (
                      <p className={`text-sm text-gray-600 font-medium mt-1 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                        {isUrdu ? 'نام:' : 'Name:'} {company.username}
                      </p>
                    )}
                    {(company?.contactNumber || company?.phone || company?.mobile) && (
                      <p className="text-sm text-gray-700 font-semibold mt-1">
                        {isUrdu ? 'فون:' : 'Phone:'} {company.contactNumber || company.phone || company.mobile}
                      </p>
                    )}
                  </div>

                  {/* Logo - Top Right */}
                  <div className="flex-shrink-0" style={{ alignSelf: 'flex-start' }}>
                    {company?.logo && (
                      <img
                        src={company.logo}
                        alt="Logo"
                        className="h-16 w-16 object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                </div>
                {company?.address && (
                  <div className="mt-2 pt-2 border-t border-gray-300">
                    <p className={`text-xs text-gray-700 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                      {company.address}
                    </p>
                  </div>
                )}
                {/* Receivable Khata Title */}
                <div className="mt-3 pt-2">
                  <h2 className={`text-lg font-bold text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                    {isUrdu ? 'قابل وصول کھاتہ' : 'Receivable Ledger'}
                  </h2>
                </div>
              </div>
            )}

            {/* Header */}
            <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border-2 border-green-200 print:hidden -mt-2">
              <div
                className={
                  isUrdu
                    ? 'flex flex-col md:flex-row md:items-center md:justify-between gap-3'
                    : 'flex items-center flex-wrap justify-between gap-4'
                }
              >
                <div className={isUrdu ? 'text-right' : ''}>
                  <h1 className={`text-2xl font-bold text-gray-900 mb-1 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? 'قابل وصول کھاتہ' : 'Receivable Ledger'}
                  </h1>
                  <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? `کل قابل وصول: ${totalReceivable.toLocaleString()}` : `Total Receivable: ${totalReceivable.toLocaleString()}`}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Download and Print Buttons */}
                  <div className="flex gap-2 print:hidden">
                    <button
                      onClick={handleDownloadPDF}
                      disabled={isGeneratingPDF}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {isGeneratingPDF ? (isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...') : (isUrdu ? 'PDF ڈاؤن لوڈ' : 'Download PDF')}
                    </button>
                    <button
                      onClick={handleDownloadExcel}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      {isUrdu ? 'Excel ڈاؤن لوڈ' : 'Download Excel'}
                    </button>
                    <button
                      onClick={handlePrint}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      {isUrdu ? 'پرنٹ' : 'Print'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Search + Date Range (Single Box) */}
            <div className="bg-white rounded-xl shadow-lg p-4 mb-4 border-2 border-green-200 print:hidden">
              {/* Search Row */}
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  placeholder={isUrdu ? 'تلاش کریں...' : 'Search...'}
                  className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  dir={isUrdu ? 'rtl' : 'ltr'}
                />
              </div>

              <div className="my-4 border-t border-gray-200" />

              {/* Date Range Row */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <div className="min-w-0">
                  <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'شروع کی تاریخ:' : 'Start Date:'}
                  </label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent"
                  />
                </div>
                <div className="min-w-0">
                  <label className={`block text-sm font-medium text-gray-700 mb-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? 'اختتام کی تاریخ:' : 'End Date:'}
                  </label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-header focus:border-transparent"
                  />
                </div>
                <div className="flex items-end gap-3 md:justify-self-end">
                  <button
                    onClick={() => setDateRange({ startDate: '', endDate: '' })}
                    className="bg-gray-500 hover:bg-gray-600 text-white font-semibold px-5 py-2 rounded-lg transition-all transform hover:scale-105"
                  >
                    {isUrdu ? 'کلیر' : 'Clear'}
                  </button>
                </div>
              </div>
            </div>

            {/* Receivable Parties Table */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-green-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-green-600 to-emerald-600 text-white">
                    <tr>
                      <th className={`px-4 py-3 text-left font-semibold ${isUrdu ? 'font-urdu text-right' : ''}`}>
                        {isUrdu ? 'نام' : 'Name'}
                      </th>
                      <th className={`px-4 py-3 text-left font-semibold ${isUrdu ? 'font-urdu text-right' : ''}`}>
                        {isUrdu ? 'قسم' : 'Type'}
                      </th>
                      <th className={`px-4 py-3 text-left font-semibold ${isUrdu ? 'font-urdu text-right' : ''}`}>
                        {isUrdu ? 'فون' : 'Phone'}
                      </th>
                      <th className={`px-4 py-3 text-left font-semibold ${isUrdu ? 'font-urdu text-right' : ''}`}>
                        {isUrdu ? 'قابل وصول' : 'Receivable Amount'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {isLoadingLedger ? (
                      <tr>
                        <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
                          <p className={`mt-4 ${isUrdu ? 'font-urdu' : ''}`}>
                            {isUrdu ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
                          </p>
                        </td>
                      </tr>
                    ) : (() => {
                      const filteredParties = ledgerParties.filter(party => {
                        const searchTerm = searchFilter.toLowerCase();
                        return (
                          party.name?.toLowerCase().includes(searchTerm) ||
                          party.nameUrdu?.toLowerCase().includes(searchTerm) ||
                          party.phone?.toLowerCase().includes(searchTerm)
                        );
                      });

                      if (filteredParties.length === 0) {
                        return (
                          <tr>
                            <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              <p className={isUrdu ? 'font-urdu' : ''}>
                                {searchFilter ? (isUrdu ? 'کوئی ڈیٹا نہیں ملا' : 'No data found') : (isUrdu ? 'ابھی کوئی قابل وصول نہیں' : 'No receivable parties yet')}
                              </p>
                            </td>
                          </tr>
                        );
                      }

                      return filteredParties.map((party) => (
                        <tr
                          key={party._id}
                          onClick={() => fetchLedgerTransactions(party._id)}
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-3">
                            <div>
                              <div className={`font-semibold text-gray-900 ${isUrdu ? 'font-urdu' : ''}`}>
                                {isUrdu ? (party.nameUrdu || party.name) : (party.name || party.nameUrdu)}
                              </div>
                              {party.nameUrdu && party.name && (
                                <div className="text-sm text-gray-500 mt-1">
                                  {isUrdu ? party.name : party.nameUrdu}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${party.type === 'customer'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-purple-100 text-purple-700'
                              }`}>
                              {party.type === 'customer'
                                ? (isUrdu ? 'گاہک' : 'Customer')
                                : (isUrdu ? 'سپلائر' : 'Supplier')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {party.phone || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-semibold text-green-600">
                              {party.currentBalance?.toLocaleString() || 0}
                            </span>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Transactions Table - Show when party is selected */}
            {selectedLedgerParty && (
              <div className="bg-white rounded-xl shadow-lg border-2 border-green-200 mt-6 print:col-span-full">
                {/* Name header - visible in print */}
                <div className="hidden print:block p-4 border-b-2 border-gray-400 mb-4">
                  <h3 className={`text-xl font-bold text-red-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                    {isUrdu ? (selectedLedgerParty.nameUrdu || selectedLedgerParty.name) : selectedLedgerParty.name}
                  </h3>
                  <p className={`text-sm text-gray-700 mt-2 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                    {isUrdu ? 'موجودہ بیلنس:' : 'Current Balance:'}{' '}
                    <span className="font-bold text-red-600 text-lg">
                      {selectedLedgerParty.currentBalance?.toLocaleString() || 0}
                    </span>
                  </p>
                </div>

                {/* Name header - hidden in print */}
                <div className="p-4 border-b border-gray-200 print:hidden">
                  <h3 className={`text-lg font-semibold text-brand-header ${isUrdu ? 'font-urdu' : ''}`}>
                    {isUrdu ? (selectedLedgerParty.nameUrdu || selectedLedgerParty.name) : selectedLedgerParty.name} - {isUrdu ? 'لین دین' : 'Transactions'}
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  {(() => {
                    // Filter transactions by date range
                    const filteredTransactions = ledgerTransactions.filter(tx => {
                      if (!dateRange.startDate && !dateRange.endDate) return true;
                      const txDate = new Date(tx.date);
                      if (dateRange.startDate && txDate < new Date(dateRange.startDate)) return false;
                      if (dateRange.endDate) {
                        const endDate = new Date(dateRange.endDate);
                        endDate.setHours(23, 59, 59, 999);
                        if (txDate > endDate) return false;
                      }
                      return true;
                    });

                    const totalDebit = filteredTransactions.reduce(
                      (sum, tx) => (tx.type === 'debit' ? sum + (tx.amount || 0) : sum),
                      0
                    );
                    const totalCredit = filteredTransactions.reduce(
                      (sum, tx) => (tx.type === 'credit' ? sum + (tx.amount || 0) : sum),
                      0
                    );
                    return (
                      <table className="w-full divide-y divide-gray-200 text-sm" style={{ tableLayout: 'fixed', width: '100%' }}>
                        <colgroup>
                          <col style={{ width: '15%' }} />
                          <col style={{ width: '35%' }} />
                          <col style={{ width: '15%' }} />
                          <col style={{ width: '15%' }} />
                          <col style={{ width: '20%' }} />
                        </colgroup>
                        <thead className="bg-brand-background">
                          <tr>
                            <th className={`px-2 py-3 text-left text-xs font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", textTransform: 'none', letterSpacing: 'normal' } : {}}>
                              {isUrdu ? 'تاریخ' : 'Date'}
                            </th>
                            <th className={`px-2 py-3 text-left text-xs font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", textTransform: 'none', letterSpacing: 'normal' } : {}}>
                              {isUrdu ? 'تفصیل' : 'Particulars'}
                            </th>
                            <th className={`px-2 py-3 text-right text-xs font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", textTransform: 'none', letterSpacing: 'normal' } : {}}>
                              {isUrdu ? 'سیل' : 'Sale'}
                            </th>
                            <th className={`px-2 py-3 text-right text-xs font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", textTransform: 'none', letterSpacing: 'normal' } : {}}>
                              {isUrdu ? 'ریکوری' : 'Recovery'}
                            </th>
                            <th className={`px-2 py-3 text-right text-xs font-semibold text-gray-700 ${isUrdu ? 'font-urdu' : 'uppercase tracking-wider'}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", textTransform: 'none', letterSpacing: 'normal' } : {}}>
                              {isUrdu ? 'بیلنس' : 'Balance'}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {/* Opening Balance */}
                          <tr>
                            <td className="px-2 py-2 text-gray-700">-</td>
                            <td className={`px-2 py-2 text-gray-700 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                              {isUrdu ? 'ابتدائی بیلنس' : 'Opening Balance'}
                            </td>
                            <td className="px-2 py-2 text-right text-gray-700">-</td>
                            <td className="px-2 py-2 text-right text-gray-700">-</td>
                            <td className="px-2 py-2 text-right font-semibold text-gray-900">
                              {selectedLedgerParty.openingBalance?.toLocaleString() || 0}
                            </td>
                          </tr>

                          {filteredTransactions.length === 0 ? (
                            <tr>
                              <td colSpan={5} className={`px-2 py-2 text-center text-gray-500 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                {isUrdu ? 'کوئی ٹرانزیکشن نہیں' : 'No transactions yet'}
                              </td>
                            </tr>
                          ) : (() => {
                            const groupedTransactions = {};
                            filteredTransactions.forEach(tx => {
                              const key = tx.billId ? tx.billId.toString() : tx._id.toString();
                              if (!groupedTransactions[key]) {
                                groupedTransactions[key] = {
                                  date: tx.date,
                                  description: tx.description || '-',
                                  sale: 0,
                                  recovery: 0,
                                  balanceAfter: tx.balanceAfter || 0,
                                  billId: tx.billId,
                                  _id: tx._id
                                };
                              }
                              if (tx.type === 'debit') {
                                groupedTransactions[key].sale += tx.amount || 0;
                              } else if (tx.type === 'credit') {
                                groupedTransactions[key].recovery += tx.amount || 0;
                              }
                              if (tx.balanceAfter && (!groupedTransactions[key].balanceAfter || new Date(tx.date) > new Date(groupedTransactions[key].date))) {
                                groupedTransactions[key].balanceAfter = tx.balanceAfter;
                              }
                            });

                            return Object.values(groupedTransactions).map((groupedTx) => (
                              <tr key={groupedTx._id} className="hover:bg-brand-background/50">
                                <td className="px-2 py-2 text-gray-700">
                                  {new Date(groupedTx.date).toLocaleDateString()}
                                </td>
                                <td className={`px-2 py-2 text-gray-700 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                                  {groupedTx.billId ? (groupedTx.description.replace(' (سیل)', '').replace(' (ریکوری)', '').replace(' (Sale)', '').replace(' (Recovery)', '') || '-') : (groupedTx.description || '-')}
                                </td>
                                <td className="px-2 py-2 text-right text-gray-700">
                                  {groupedTx.sale > 0 ? groupedTx.sale.toLocaleString() : ''}
                                </td>
                                <td className="px-2 py-2 text-right text-gray-700">
                                  {groupedTx.recovery > 0 ? groupedTx.recovery.toLocaleString() : ''}
                                </td>
                                <td className="px-2 py-2 text-right font-semibold text-gray-900">
                                  {groupedTx.balanceAfter?.toLocaleString() || ''}
                                </td>
                              </tr>
                            ));
                          })()}

                          {/* Closing Balance */}
                          <tr className="bg-brand-background">
                            <td className={`px-2 py-2 text-gray-700 font-semibold ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}} colSpan={2}>
                              {isUrdu ? 'اختتامی بیلنس' : 'Closing Balance'}
                            </td>
                            <td className="px-2 py-2 text-right text-gray-900 font-semibold">
                              {totalDebit.toLocaleString()}
                            </td>
                            <td className="px-2 py-2 text-right text-gray-900 font-semibold">
                              {totalCredit.toLocaleString()}
                            </td>
                            <td className="px-2 py-2 text-right font-semibold text-brand-header">
                              {selectedLedgerParty.currentBalance?.toLocaleString() || 0}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:col-span-full {
            grid-column: 1 / -1 !important;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            margin: 0.5cm;
          }
          .grid {
            display: block !important;
          }
          .md\\:grid-cols-3 {
            display: block !important;
          }
          .bg-white {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0.5cm !important;
          }
          .rounded-lg {
            border-radius: 0 !important;
          }
          .shadow-lg {
            box-shadow: none !important;
          }
          table {
            width: 100% !important;
            max-width: 100% !important;
            table-layout: auto !important;
          }
          .overflow-x-auto {
            width: 100% !important;
            overflow: visible !important;
          }
        }
      `}</style>
    </>
  );
}

export default ReceivableKhata;

